import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, ExternalLink } from 'lucide-react';
import { getBugHygieneDashboard } from '../services/api';

// The colour follows the age: green while it is fresh, yellow as it ages, red once it is old.
const COLUMNS = [
  { key: 'd01', label: '<3d', hint: 'under 3 days (0-2 days)', tone: 'green' },
  { key: 'd3', label: '3d', hint: '3-4 days', tone: 'yellow' },
  { key: 'd5', label: '≥5d', hint: '5 or more days', tone: 'red' }
];
// Ready for QA only cares whether a bug has waited more than a day.
const READY_COLUMNS = [
  { key: 'd0', label: '<1d', hint: 'under 1 day', tone: 'green' },
  { key: 'gt1', label: '>1d', hint: '1 calendar day or more (the SLA card counts business days)', tone: 'red' }
];
const PAGE_SIZE = 25;
const PRIORITIES = ['High', 'Medium', 'Low'];

/** Rebuilds a table (rows per person plus totals) from the bug list, so the page can re-count for the chosen priorities. */
function aggregate(bugs, table) {
  const rows = new Map();
  bugs.filter(b => b.table === table).forEach(b => {
    const id = b.person_key || b.person;
    const r = rows.get(id) || { person: b.person, key: id, d0: 0, d1: 0, d3: 0, d5: 0, total: 0, no_attachments: 0, wrong_assignee: 0 };
    r[b.bucket] += 1;
    r.total += 1;
    if (table === 'open' && b.attachments === 0) r.no_attachments += 1;
    if (table === 'open' && b.wrong_assignee) r.wrong_assignee += 1;
    rows.set(id, r);
  });
  const ordered = [...rows.values()].sort((a, b) => b.total - a.total || a.person.localeCompare(b.person));
  const totals = {};
  ['d0', 'd1', 'd3', 'd5', 'total', 'no_attachments', 'wrong_assignee'].forEach(k => { totals[k] = ordered.reduce((n, r) => n + r[k], 0); });
  return { rows: ordered, totals };
}
const BUCKET_TONE = { d0: 'green', d1: 'green', d3: 'yellow', d5: 'red' };

function formatTime(iso) {
  return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// The collector runs twice a day; a snapshot older than this means it has stopped.
const STALE_AFTER_MS = 26 * 3600 * 1000;
const isStale = iso => Date.now() - new Date(iso).getTime() > STALE_AFTER_MS;

// The two youngest buckets share one column.
const count = (row, key) => (key === 'd01' ? (row?.d0 || 0) + (row?.d1 || 0)
  : key === 'gt1' ? (row?.d1 || 0) + (row?.d3 || 0) + (row?.d5 || 0) : row?.[key]);

function Cell({ value, tone }) {
  if (!value) return <td className="bh-num bh-zero">&ndash;</td>;
  return <td className={`bh-num ${tone ? `bh-${tone}` : ''}`}>{value}</td>;
}

/** The headline numbers above the tables. A card turns green when its count is zero. */
const CARDS = [
  { key: 'missing_evidence', title: 'New / Open bugs missing evidence', note: 'No attachment (logs/screenshots) in Taskei', tone: 'red' },
  { key: 'overdue_followup', split: 'overdue_by_priority', title: 'Open bugs overdue for follow-up', note: 'Last comment (or creation) older than 1 day', tone: 'yellow' },
  { key: 'ready_sla_breaches', title: 'Ready for QA SLA breaches', note: 'Pending over 1 business day', tone: 'red' },
  { key: 'severely_aged', split: 'severe_by_priority', title: 'Severely aged exceptions', note: 'Open + Ready for QA aged ≥5 days', tone: 'red' }
];

export function SummaryCards({ cumulative }) {
  if (!cumulative) return null;
  return (
    <section className="bh-cards" aria-label="Summary">
      {CARDS.map(card => {
        const value = cumulative[card.key] ?? 0;
        return (
          <div key={card.key} className="bh-card">
            <span className="bh-card-title">{card.title}</span>
            <span className={`bh-card-value bh-${value === 0 ? 'green' : card.tone}`}>{value}</span>
            <span className="bh-card-note">{card.note}</span>
            {card.split && cumulative[card.split] && (
              <div className="bh-split" aria-label={`${card.title} by priority`}>
                {['High', 'Medium', 'Low', 'Unset'].filter(p => p !== 'Unset' || cumulative[card.split].Unset > 0).map(p => (
                  <div key={p} className={`bh-split-item bh-prio-${p.toLowerCase()}`}>
                    <span className="bh-split-label">{p}</span>
                    <span className="bh-split-count">{cumulative[card.split][p] || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** One table: a row per person, a column per age bucket, totals at the bottom. */
export function HygieneTable({ title, subtitle, table, extras = [], columns = COLUMNS, selected, onSelect, emptyText, personLabel = 'Person' }) {
  const rows = table?.rows || [];
  const totals = table?.totals || {};
  return (
    <section className="tr-panel auto-section-full">
      <div className="tr-panel-head">
        <h3>{title}</h3>
        <span className="tr-panel-sub">{subtitle}</span>
      </div>
      <div className="tr-panel-body">
        {totals.total === 0 && <p className="bh-empty">{emptyText}</p>}
        <table className="bh-table">
          <thead>
            <tr>
              <th>{personLabel}</th>
              {columns.map(c => <th key={c.key} className="bh-num" title={c.hint}>{c.label}</th>)}
              {extras.map(x => <th key={x.key} className="bh-num" title={x.hint}>{x.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} className={`${selected === r.person ? 'bh-selected' : ''} ${r.total ? 'bh-clickable' : ''}`}
                onClick={() => r.total && onSelect(selected === r.person ? '' : r.person)}>
                <td>{r.person}</td>
                {columns.map(c => <Cell key={c.key} value={count(r, c.key)} tone={c.tone} />)}
                {extras.map(x => <Cell key={x.key} value={r[x.key]} tone="red" />)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>All</td>
              {columns.map(c => <td key={c.key} className={`bh-num ${count(totals, c.key) ? `bh-${c.tone}` : ''}`}>{count(totals, c.key) || 0}</td>)}
              {extras.map(x => <td key={x.key} className={`bh-num ${totals[x.key] ? 'bh-red' : ''}`}>{totals[x.key] || 0}</td>)}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function BugHygieneDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [person, setPerson] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    getBugHygieneDashboard()
      .then(setData)
      .catch(err => setError(err.message || 'Failed to load dashboard data'))
      .finally(() => setLoading(false));
  }, []);

  const [priority, setPriority] = useState('All');
  const inScope = useMemo(() => (data?.bugs || []).filter(b => priority === 'All' || b.priority === priority), [data, priority]);
  const openTable = useMemo(() => aggregate(inScope, 'open'), [inScope]);
  const readyTable = useMemo(() => aggregate(inScope, 'ready'), [inScope]);
  const bugs = useMemo(() => inScope.filter(b => !person || b.person === person), [inScope, person]);
  useEffect(() => setPage(1), [person, priority]);
  const pages = Math.max(1, Math.ceil(bugs.length / PAGE_SIZE));
  const shown = bugs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="auto-page">
      <div className="auto-header">
        <div className="auto-header-left">
          <button className="auto-back-btn" onClick={() => navigate('/dashboards')}><ArrowLeft size={16} /></button>
          <div>
            <h1 className="auto-title">Bug Hygiene</h1>
            <p className="auto-subtitle">Bugs created by the Hawkeye team on the APEX Bug Triage board, by how long they have been waiting</p>
          </div>
        </div>
        {data?.available && (
          <div className="auto-header-right"><span className={`tr-panel-sub ${isStale(data.generated_at) ? 'bh-stale' : ''}`}>
            Updated {formatTime(data.generated_at)}{isStale(data.generated_at) ? ' (out of date: the collector may have stopped)' : ''}</span></div>
        )}
      </div>

      {loading ? (
        <div className="tr-loading"><RefreshCw size={20} className="spin" /> Loading...</div>
      ) : error ? (
        <div className="tr-nodata"><h3>Error loading data</h3><p>{error}</p></div>
      ) : !data?.available ? (
        <div className="tr-nodata"><h3>No data available</h3><p>The first snapshot has not been generated yet.</p></div>
      ) : (
        <div className="ss-layout">
          {(data.bugs || []).length === 0 && (
            <div className="tr-nodata" role="status">
              <h3>No bugs were counted in this snapshot</h3>
              <p>
                {data.stats?.open_bugs > 0
                  ? (data.stats.new_open_bugs + data.stats.ready_for_qa_bugs === 0
                    ? `${data.stats.open_bugs} bug(s) were read from the board, but none are in New, Open or Ready for QA (${data.stats.off_table || 0} are in other workflow steps).`
                    : `${data.stats.open_bugs} bug(s) were read from the board, but none were created by the Hawkeye team (${data.stats.outside_team || 0} created by others).`)
                  : 'Taskei returned no bugs for the board in the last run.'}
              </p>
            </div>
          )}
          <SummaryCards cumulative={data.cumulative} />

          <div className="bh-priority" role="group" aria-label="Priority">
            <span className="tr-panel-sub">Priority</span>
            {['All', ...PRIORITIES, ...((data.bugs || []).some(b => b.priority === 'Unset') ? ['Unset'] : [])].map(p => (
              <button key={p} className={`btn btn-sm ${priority === p ? 'btn-primary' : 'btn-secondary'}`}
                aria-pressed={priority === p} onClick={() => setPriority(p)}>{p}</button>
            ))}
            <span className="tr-panel-sub">Filters the tables and list below. The cards above always count every priority.</span>
          </div>

          <HygieneTable
            title="New / Open bugs"
            subtitle="By bug creator; age since the latest comment, or since creation if there are no comments"
            personLabel="Creator"
            table={openTable}
            extras={[
              { key: 'no_attachments', label: 'No attachments', hint: 'Bugs in this table with no attachment in Taskei' },
              { key: 'wrong_assignee', label: 'Incorrect assignee / unassigned', hint: 'Bugs this person created that have no assignee or are assigned to a Hawkeye team member' }
            ]}
            selected={person} onSelect={setPerson}
            emptyText="There are no New or Open bugs on the board." />

          <HygieneTable
            title="Ready for QA"
            subtitle="By bug creator; days since the bug entered Ready for QA and is still not closed"
            personLabel="Creator"
            columns={READY_COLUMNS}
            table={readyTable}
            selected={person} onSelect={setPerson}
            emptyText="There are no bugs in Ready for QA." />

          <section className="tr-panel auto-section-full">
            <div className="tr-panel-head">
              <h3>Bugs{person ? ` for ${person}` : ''}</h3>
              {person
                ? <button className="btn btn-secondary btn-sm" onClick={() => setPerson('')}>Show everyone</button>
                : <span className="tr-panel-sub">Select a person above to filter</span>}
            </div>
            <div className="tr-panel-body">
              {bugs.length === 0 ? <p className="bh-empty">Nothing to list.</p> : (
                <table className="bh-table bh-bugs">
                  <thead>
                    <tr><th>Bug</th><th>List</th><th>Creator</th><th>Assignee</th><th className="bh-num">Age</th><th className="bh-num">Attachments</th><th>Priority</th><th>Title</th></tr>
                  </thead>
                  <tbody>
                    {shown.map(b => (
                      <tr key={b.short_id + b.table}>
                        <td><a href={b.url} target="_blank" rel="noopener noreferrer">{b.short_id} <ExternalLink size={11} /></a></td>
                        <td>{b.table === 'open' ? 'New / Open' : 'Ready for QA'}</td>
                        <td>{b.person}</td>
                        <td className={b.wrong_assignee ? 'bh-red' : undefined} title={b.wrong_assignee ? 'No assignee, or assigned to a Hawkeye team member instead of a developer' : undefined}>
                          {b.assignee || 'Unassigned'}
                        </td>
                        <td className={`bh-num bh-${BUCKET_TONE[b.bucket] || ''}`} title={b.age_estimated ? 'Estimated: already in Ready for QA when tracking began' : b.age_basis ? `Counted from ${b.age_basis}` : undefined}>
                          {b.age_estimated ? '~' : ''}{b.age_days}d
                        </td>
                        <td className={`bh-num ${b.attachments === 0 ? 'bh-red' : ''}`}>{b.attachments}</td>
                        <td>{b.priority}</td>
                        <td className="bh-title" title={b.title}>{b.title}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {pages > 1 && (
                <nav className="bh-pager" aria-label="Bug list pages">
                  <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                  {Array.from({ length: pages }, (_, i) => i + 1).map(n => (
                    <button key={n} className={`btn btn-sm ${n === page ? 'btn-primary' : 'btn-secondary'}`} aria-current={n === page ? 'page' : undefined} onClick={() => setPage(n)}>{n}</button>
                  ))}
                  <button className="btn btn-secondary btn-sm" disabled={page === pages} onClick={() => setPage(page + 1)}>Next</button>
                  <span className="tr-panel-sub">{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, bugs.length)} of {bugs.length}</span>
                </nav>
              )}
            </div>
          </section>

          <div className="tr-refresh-info bh-notes">
            <span>
              Only bugs on the APEX Bug Triage board that were created by Hawkeye team members are counted (their team label does not matter). Buckets: &lt;3d = under 3 days, 3d = 3-4 days, &ge;5d = 5 or more days.</span>
            <span>
              Table 1 lists every New or Open bug by its creator; its age runs from the latest comment (any author), or from creation when there are no comments. Table 2 is per bug creator (the assignee is usually the developer) and counts every bug that is still open in Ready for QA, by days since it entered that step, whoever has updated it since. A ~ marks a bug already in Ready for QA when tracking began, so its age counts from its last update and may be understated. The Ready for QA SLA uses business days (weekends excluded, Central time; holidays are not excluded). Colours go green, yellow, red as the age grows.
              {data.stats?.unverified > 0 && ` ${data.stats.unverified} bug(s) could not be checked this run and are not counted.`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default BugHygieneDashboard;
